import React, { Fragment, useEffect, useState } from "react";
import {
  CircularProgress,
  createTheme,
  Divider,
  Grid,
  Paper,
  SwipeableDrawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import CustomDrawer from "../../components/drawer/drawer.component";
import MetricBoxComponent from "../../components/orders/metricBox.component";
import "./schemes.styles.scss";

const theme = createTheme({
  palette: {
    primary: {
      main: "#a36e29",
    },
  },
  typography: {
    fontFamily: '"Work Sans", sans-serif',
  },
});

const formatAmount = (amt) => `₹ ${parseFloat(amt || 0).toLocaleString()}`;

const SchemesComponent = () => {
  const admin = JSON.parse(localStorage.getItem("admin"));
  const navigate = useNavigate();

  const [schemesList, setSchemesList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingTransaction, setLoadingTransaction] = useState(false);

  const [stats, setStats] = useState({
    total_schemes: 0,
    total_active: 0,
    total_inactive: 0,
    total_paid: 0,
  });

  useEffect(() => {
    if (!admin?.token) return;
    const fetchSchemes = async () => {
      try {
        const params = new URLSearchParams({
          status: "active",
          page: "1",
          limit: "10",
        });
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}v2.0.0/admin/schemes/enrollments.php?${params}`,
          {
            headers: {
              Authorization: `Bearer ${admin.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (data.success === true) {
          const response = data.data?.enrollments || [];
          setSchemesList(response);
          setStats({
            total_schemes: response.length,
            total_active: response.filter((s) => s.status === "active").length,
            total_inactive: response.filter((s) => s.status !== "active")
              .length,
            total_paid: response.reduce(
              (acc, cur) => acc + parseFloat(cur.payment_summary?.total_paid_amount || 0),
              0
            ),
            //   total_paid: response.reduce(
            //     (acc, cur) => acc + response.payment_summary?.total_paid_amount || 0)
            // });
          });
        }
      } catch (err) {
        console.error("Error fetching schemes:", err);
      }
    };
    fetchSchemes();
  }, [admin?.token]);

  const fetchTransactionDetails = async (scheme) => {
    setSelectedScheme(scheme);
    setLoadingTransaction(true);
    try {
      const params = new URLSearchParams({
        status: "active",
        page: "1",
        limit: "10",
      });
      const { data } = await axios.get(
        {
          /*`https://api.sadashrijewelkart.com/v2.0.0/admin/schemes/transactions.php?subscription=${scheme.id}`*/
        }`${process.env.REACT_APP_API_URL}v2.0.0/admin/schemes/enrollments.php?${params}`,
        {
          headers: {
            Authorization: `Bearer ${admin.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSelectedTransaction(
        data.success === true && data.data.enrollments.length > 0
          ? data.data?.enrollments[0]
          : null
      );
    } catch (error) {
      console.error("Failed to fetch transaction details:", error);
      setSelectedTransaction(null);
    } finally {
      setLoadingTransaction(false);
      setDialogOpen(true);
    }
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <div className="product">
      <CustomDrawer section="schemes" />
      <Divider orientation="vertical" flexItem className="divider" />

      <div className="category-component">
        <ToastContainer />

        <ThemeProvider theme={theme}>
          <SwipeableDrawer
            anchor="right"
            open={showDrawer}
            onClose={() => setShowDrawer(false)}
            onOpen={() => setShowDrawer(true)}
          />
        </ThemeProvider>

        <div className="head">
          <div className="head-txt">Scheme Subscriptions</div>
        </div>

        <Grid
          container
          spacing={3}
          sx={{ width: "100%", height: 300, pl: 3, mb: 3 }}
        >
          <Grid item xs={3}>
            <MetricBoxComponent
              heading="Total Subscriptions"
              metric={stats.total_schemes.toString()}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBoxComponent
              heading="Active Subscriptions"
              metric={stats.total_active.toString()}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBoxComponent
              heading="Inactive Subscriptions"
              metric={stats.total_inactive.toString()}
            />
          </Grid>
          <Grid item xs={3}>
            <MetricBoxComponent
              heading="Total Paid"
              metric={`₹ ${stats.total_paid}`}
            />
          </Grid>
        </Grid>

        <Divider />

        <ThemeProvider theme={theme}>
          <Paper
            sx={{
              width: "95%",
              overflow: "hidden",
              height: "50vh",
              m: "30px 25px",
              borderRadius: 2,
              boxShadow: 1,
              pl: 1,
            }}
          >
            {schemesList.length === 0 ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ height: "90%" }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>User ID</TableCell>
                      <TableCell>Scheme ID</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start Date</TableCell>
                      {/*<TableCell>Exp. Closure</TableCell>*/}
                      <TableCell>Total Paid</TableCell>
                      <TableCell>Installments Paid</TableCell>
                      {/*<TableCell>Plan</TableCell>*/}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemesList
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => fetchTransactionDetails(row)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.user_id}</TableCell>
                          <TableCell>{row.scheme_id}</TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 800,
                              color: row.status === "active" ? "green" : "red",
                            }}
                          >
                            {row.status}
                          </TableCell>
                          <TableCell>
                            {new Date(row.enrollment_date).toLocaleDateString()}
                          </TableCell>
                          {/*
                        <TableCell>
                          {row.exp_closure_date === "0000-00-00 00:00:00"
                            ? "N/A"
                            : new Date(row.exp_closure_date).toLocaleDateString()}
                        </TableCell>
                        */}
                          <TableCell>
                            ₹ {row.payment_summary?.total_paid_amount}
                          </TableCell>
                          <TableCell>
                            {row.payment_summary?.paid_installments}
                          </TableCell>
                          {/*<TableCell>{row.plan}</TableCell>*/}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={schemesList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </ThemeProvider>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pr: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  onClick={() => setDialogOpen(false)}
                  edge="start"
                  sx={{ mr: 1 }}
                >
                  <CloseIcon />
                </IconButton>
                <Box>
                  <Typography fontWeight="bold" variant="h6">
                    {selectedScheme?.scheme_name || "Scheme Name"}
                  </Typography>
                  <Typography fontSize="0.9rem" color="text.secondary">
                    {selectedScheme?.start_date || "Start Date"} –{" "}
                    {selectedScheme?.exp_closure_date || "Maturity Date"}
                  </Typography>
                </Box>
              </Box>

              <Box textAlign="right">
                <Chip
                  label={selectedScheme?.status || "In Progress"}
                  size="small"
                  sx={{
                    backgroundColor:
                      selectedScheme?.status === "active"
                        ? "#4caf50"
                        : "#f57c00",
                    color: "white",
                    fontWeight: "bold",
                    mb: 0.5,
                  }}
                />
                <Typography fontSize="0.9rem">
                  <b>Total Payment:</b>{" "}
                  {formatAmount(
                    selectedScheme?.payment_summary?.total_paid_amount || 0
                  )}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <Divider />

          <Divider />

          <DialogContent sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Transaction History
            </Typography>

            {selectedTransaction ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography>
                  <strong>Payment ID:</strong>{" "}
                  {selectedTransaction.razorpay_subscription_id || "N/A"}
                </Typography>
                <Typography>
                  <strong>Amount:</strong> ₹{" "}
                  {selectedTransaction.amount || "N/A"}
                </Typography>
                <Typography>
                  <strong>Status:</strong> {selectedTransaction.status || "N/A"}
                </Typography>
                <Typography>
                  <strong>Created At:</strong>{" "}
                  {selectedTransaction.created_at
                    ? new Date(selectedTransaction.created_at).toLocaleString()
                    : "N/A"}
                </Typography>
                <Typography>
                  <strong>Gold Rate:</strong> ₹{" "}
                  {(() => {
                    try {
                      const rateData = JSON.parse(
                        selectedTransaction.accumulation_metal_rate
                      );
                      return rateData?.jewelry_prices?.gold22 || "N/A";
                    } catch {
                      return "N/A";
                    }
                  })()}{" "}
                  /gm
                </Typography>
              </Box>
            ) : loadingTransaction ? (
              <Box textAlign="center">
                <CircularProgress />
              </Box>
            ) : (
              <Typography>Transaction data not found.</Typography>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SchemesComponent;
